import PropTypes from 'prop-types';

import {
  Box,
  Image,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import BaseLink from '@amblique/base/app/components/link';
import {urlHelper} from '@amblique/base/app/helpers/url-helper';
import {Component as ContentfulComponent} from '@amblique/king-living/app/components/contentful/component';

/**
 * Resolve the click-through URL authored on a referenced Image entry.
 * KL-1127 — CF-022 (and similar overlays) sit above the media link, so the
 * overlay must reuse the Image URL or the card appears non-clickable.
 *
 * @param {Contentful.CarouselItem['media']} media
 * @returns {{linkUrl: string, isExternal: boolean, openInNewTab: boolean}|null}
 */
function getMediaLink(media) {
  if (!media || media.__typename !== 'Image' || !media.url) {
    return null;
  }

  const {url, openInNewTab} = media;
  const isExternal =
    url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//');
  const parsedUrl = !isExternal ? urlHelper.parseStorefrontUrl(url) : null;
  const linkUrl = parsedUrl?.appUrl || url;

  if (!linkUrl) {
    return null;
  }

  return {linkUrl, isExternal, openInNewTab: Boolean(openInNewTab)};
}

/**
 * Overlay wrapper: when the referenced Image has a URL, the absolute text
 * overlay itself becomes the clickable target (same destination as the image).
 *
 * @param {object} props
 * @param {object} props.styles - Chakra multi-style parts for textContainer
 * @param {{linkUrl: string, isExternal: boolean, openInNewTab: boolean}|null} props.mediaLink
 * @param {string} [props.ariaLabel]
 * @param {React.ReactNode} props.children
 */
const TextOverlay = ({styles, mediaLink, ariaLabel, children}) => {
  if (!mediaLink) {
    return <Box {...styles.textContainer}>{children}</Box>;
  }

  const {linkUrl, isExternal, openInNewTab} = mediaLink;

  return (
    <BaseLink
      aria-label={ariaLabel || undefined}
      className="carousel-item-overlay-link"
      href={isExternal ? linkUrl : undefined}
      rel={openInNewTab ? 'noopener noreferrer' : undefined}
      target={openInNewTab ? '_blank' : '_self'}
      to={!isExternal ? linkUrl : undefined}
      {...styles.textContainer}
      _hover={{textDecoration: 'none', color: 'inherit'}}
    >
      {children}
    </BaseLink>
  );
};

TextOverlay.propTypes = {
  ariaLabel: PropTypes.string,
  children: PropTypes.node,
  mediaLink: PropTypes.shape({
    isExternal: PropTypes.bool.isRequired,
    linkUrl: PropTypes.string.isRequired,
    openInNewTab: PropTypes.bool.isRequired,
  }),
  styles: PropTypes.object.isRequired,
};

/**
 * CarouselItem component
 * Override this component in your package to provide custom implementation.
 *
 * @param {Object} props - Component props
 * @param {Contentful.CarouselItem} props.component - CarouselItem component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
export const CarouselItem = ({component, isComplete = true, isFetching = false, ...props}) => {
  const {appearance, media, text1, text2, text3, text3Icon, text4} = component;

  const styles = useMultiStyleConfig('contentful/carousel-item', {
    component,
    isComplete,
    isFetching,
    variant: appearance,
  });

  // KL-1127 — reuse Image entry URL on overlay text so CF-022 cards are clickable.
  const mediaLink = getMediaLink(media);
  const overlayAriaLabel = [text3, text2, text1, text4].filter(Boolean).join(' — ') || undefined;

  const text3Block = (text3 || text3Icon?.url) && (
    <Box {...styles.text3Row}>
      {text3Icon?.url ? (
        <Image
          alt={text3Icon.description || text3Icon.title || ''}
          loading="lazy"
          src={text3Icon.url}
          {...styles.text3Icon}
        />
      ) : null}
      {text3 ? <Text {...styles.text3}>{text3}</Text> : null}
    </Box>
  );

  return (
    <Box {...styles.root}>
      {appearance === 'CF-011' ? (
        <Box {...styles.textContainer}>
          <Box {...styles.group1}>
            <Text {...styles.text1}>{text1}</Text>
          </Box>
        </Box>
      ) : appearance === 'CF-012.01' ? (
        <TextOverlay ariaLabel={overlayAriaLabel} mediaLink={mediaLink} styles={styles}>
          <Box {...styles.group1} data-carousel-heading>
            <Text {...styles.text1}>{text1}</Text>
            <Text {...styles.text2}>{text2}</Text>
          </Box>

          <Box {...styles.group2}>
            {text3Block}
            <Text {...styles.text4}>{text4}</Text>
          </Box>
        </TextOverlay>
      ) : appearance === 'CF-022' ? (
        <TextOverlay ariaLabel={overlayAriaLabel} mediaLink={mediaLink} styles={styles}>
          <Box {...styles.group1} data-carousel-heading>
            <Text {...styles.text2}>{text2}</Text>
            <Text {...styles.text1}>{text1}</Text>
          </Box>

          <Box {...styles.group2}>
            {text3Block}
            <Text {...styles.text4}>{text4}</Text>
          </Box>
        </TextOverlay>
      ) : appearance === 'CF-012.02' ? (
        <>
          <Box {...styles.group1} data-carousel-heading>
            <Text {...styles.text1}>{text1}</Text>
            <Text {...styles.text2}>{text2}</Text>
          </Box>
        </>
      ) : appearance === 'CF-013' ? (
        <>
          <Box {...styles.group1}>
            <Text {...styles.text1}>{text1}</Text>
            <Text {...styles.text2}>{text2}</Text>
            <Text {...styles.text3}>{text3}</Text>
          </Box>
        </>
      ) : null}

      {media && <ContentfulComponent component={media} {...styles.media} />}
    </Box>
  );
};

CarouselItem.displayName = 'CarouselItem';
CarouselItem.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
